/*  ========================================================================
    # Solutions
    ========================================================================  */

import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import useLocalization from '../../hooks/useLocalization';
import useTranslation from '../../hooks/useTranslation';
import { useSwipeable } from 'react-swipeable';

const Solutions = ({ headline, solutions }) => {
  const local = useLocalization();
  const __ = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  const floor = 5;

  /**
   * If there are fewer than 5 entries, the array is padded with existing ones.
   */
  const sliderEntries = useMemo(() => {
    let tempSolutions = [];

    if (solutions.length > 1) {
      tempSolutions = [...solutions];
      while (tempSolutions.length < floor) tempSolutions.push(...solutions);
    } else {
      tempSolutions = solutions;
    }

    return tempSolutions;
  }, [solutions]);

  /**
   *
   */
  const getDataOffset = index => {
    const delta = index - activeSlide;

    if (activeSlide === 0 && index === sliderEntries.length - 1) return -1;
    if (activeSlide === 0 && index === sliderEntries.length - 2) return -2;
    if (activeSlide === 0 && index === sliderEntries.length - 3) return -3;

    if (activeSlide === 1 && index === sliderEntries.length - 1) return -2;
    if (activeSlide === 1 && index === sliderEntries.length - 2) return -3;

    if (activeSlide === 2 && index === sliderEntries.length - 1) return -3;

    if (activeSlide === sliderEntries.length - 1 && index === 0) return 1;
    if (activeSlide === sliderEntries.length - 1 && index === 1) return 2;
    if (activeSlide === sliderEntries.length - 1 && index === 2) return 3;

    if (activeSlide === sliderEntries.length - 2 && index === 0) return 2;
    if (activeSlide === sliderEntries.length - 2 && index === 1) return 3;

    if (activeSlide === sliderEntries.length - 3 && index === 0) return 3;

    if (delta <= -3) return -3;
    if (delta >= 3) return 3;

    return delta;
  };

  const handleScrollDown = () => {
    if (activeSlide < sliderEntries.length - 1) {
      setActiveSlide(prev => prev + 1);
    } else {
      setActiveSlide(0);
    }
  };
  const handleScrollUp = () => {
    if (activeSlide === 0) {
      setActiveSlide(sliderEntries.length - 1);
    } else {
      setActiveSlide(prev => prev - 1);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleScrollDown,
    onSwipedRight: handleScrollUp,
  });

  return (
    <div className='container'>
      {headline ? (
        <div className='wysiwyg-block' dangerouslySetInnerHTML={{ __html: headline }} />
      ) : null}
      <div className='s-solutions__wrapper'>
        <div className='s-solutions__slider' {...swipeHandlers}>
          <button
            type='button'
            className='c-button c-button--icon s-solutions__control-top'
            title={__('section-solutions-back')}
            onClick={() => handleScrollUp()}
          >
            <svg viewBox='0 0 24 24'>
              <use href={`${local('svgSprite')}#angle-up`} />
            </svg>
          </button>
          <div className='s-solutions__icons'>
            {sliderEntries.map((solution, index) => (
              <button
                type='button'
                key={index}
                className='s-solutions__icon'
                data-offset={getDataOffset(index)}
                onClick={() => setActiveSlide(index)}
                title={solution.title}
              >
                <div className='s-solutions__icon-wrapper' dangerouslySetInnerHTML={{ __html: solution.icon }} />
                <span dangerouslySetInnerHTML={{ __html: solution.title }} />
              </button>
            ))}
          </div>
          <button
            type='button'
            className='c-button c-button--icon s-solutions__control-bottom'
            title={__('section-solutions-forward')}
            onClick={() => handleScrollDown()}
          >
            <svg viewBox='0 0 24 24'>
              <use href={`${local('svgSprite')}#angle-down`} />
            </svg>
          </button>
        </div>
        <div className='s-solutions__slider-content'>
          <div className='s-solutions__content-wrapper'>
            {sliderEntries.map((solution, index) => (
              <div
                className='s-solutions__content'
                data-offset={getDataOffset(index)}
                key={`${index}-solution-content`}
              >
                <div
                  className='wysiwyg-block'
                  dangerouslySetInnerHTML={{ __html: solution.content }}
                />
                {solution.link ?
                  <a
                    href={solution.link.url}
                    title={solution.link.title}
                    target={solution.link.target}
                    className='u-link-with-svg u-link-with-svg--arrow-white'
                  >
                    {solution.link.title}
                  </a>
                  : null}
              </div>
            ))}
          </div>
          {/* Speech bubble */}
          <div className='s-solutions__content-icon'>
            <div className="s-solutions__content-icon-wrapper">
              {sliderEntries.map((solution, index) => (
                <span dangerouslySetInnerHTML={{ __html: solution.icon }} data-offset={getDataOffset(index)} key={`${index}-solution-icon`}></span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

Solutions.propTypes = {
  headline: PropTypes.string,
  solutions: PropTypes.array
};

export default Solutions;
